import {
  MULTIPLE_PROCEDURE_METHODOLOGY,
  reimbursementSlotLabel,
  reimbursementSlotOf,
} from "pages/PayerRules/contracts/helpers";

import {
  IApplyContractTermChange,
  IApplyContractTermDocuments,
  IApplyContractTermRequest,
  IApplyContractTermResult,
  IApplyContractTermTarget,
} from "../../../store/queries/contractModelDrafts";
import { CONTRACT_MODEL } from "../copy";

/**
 * CLE-2811 — how the "save to contract" preview labels each field. Pure, so the wording the reviewer
 * relies on ("will replace" vs "will fill") is tested without a DOM.
 */

export interface IApplyActionLabel {
  label: string;
  severity: "success" | "info" | "warning" | "danger";
}

export const APPLY_ACTION_LABELS: Record<
  IApplyContractTermChange["action"],
  IApplyActionLabel
> = {
  filled: { label: "Will fill", severity: "success" },
  // Overwrites something a person may have typed — the one label that must stand out.
  replaced: { label: "Will replace", severity: "danger" },
  appended: { label: "Will append", severity: "info" },
  skipped: { label: "Left as is", severity: "warning" },
};

/** The preview's one-line summary: what the save would do, by kind, in a fixed order. */
export const summarizeApplyPlan = (
  changes: IApplyContractTermChange[]
): string => {
  const count = (a: IApplyContractTermChange["action"]) =>
    changes.filter((c) => c.action === a).length;
  const parts = [
    [count("filled"), "will fill"],
    [count("replaced"), "will replace"],
    [count("appended"), "will append"],
    [count("skipped"), "left as is"],
  ]
    .filter(([n]) => (n as number) > 0)
    .map(([n, what]) => `${n} ${what}`);
  return parts.length > 0 ? parts.join(" · ") : "Nothing to save";
};

/** How the preview names each contract field it would touch (the reimbursement slots: `applyFieldLabel`). */
const APPLY_FIELD_LABELS: Record<string, string> = {
  contractType: "Contract type",
  signedDate: "Signed date",
  timelyFilingDays: "Timely filing (days)",
  implantTerms: "Implant terms",
  // CLE-2811 (2026-09-28) — its own contract field now, no longer a line appended to the notes.
  multipleProcedureMethodology: MULTIPLE_PROCEDURE_METHODOLOGY,
  nextUpdateDate: "Next update date",
  updateTerm: "Update term",
  updatePercent: "Percent increase",
  notes: "Notes",
};

/**
 * The contract type the contract will have after the save — what decides how its reimbursement slots
 * are labelled: the contract's own when it keeps it ("left as is"), else the one the save fills, else the
 * term's.
 */
export const contractTypeAfterSave = (
  changes: IApplyContractTermChange[],
  proposedModeling: unknown
): string | null => {
  const change = changes.find((c) => c.field === "contractType");
  if (change)
    return (change.action === "skipped" ? change.from : change.to) ?? null;
  const type =
    proposedModeling && typeof proposedModeling === "object"
      ? (proposedModeling as Record<string, unknown>).contractType
      : null;
  return typeof type === "string" ? type : null;
};

/**
 * One preview row's label. A reimbursement slot reads as the contract's own pages do
 * (`reimbursementSlotLabel`): "Reimbursement schedule" on an ASC contract, "Outpatient reimbursement
 * schedule" where there are two.
 */
export const applyFieldLabel = (
  field: string,
  contractType: string | null
): string => {
  const slot = reimbursementSlotOf(field);
  if (slot) return reimbursementSlotLabel(slot, contractType);
  return APPLY_FIELD_LABELS[field] ?? field;
};

/** True when the save would write anything at all — the button is disabled otherwise. */
export const planWritesSomething = (
  changes: IApplyContractTermChange[]
): boolean => changes.some((c) => c.action !== "skipped");

// ---------------------------------------------------------------------------------------------
// CLE-2811 — where the save goes. One document often prices several payers (Medflex, SuperMed, ACA),
// one contract term each, and a run belongs to ONE contract. The server resolves the target
// (`target` on the dry run); these turn its answer into what the panel says and sends.
// ---------------------------------------------------------------------------------------------

/**
 * `default`: the term has a home without asking (the run's contract, or a standalone run's new draft).
 * `pick`: an off-payer term with existing contracts for its payer — she chooses one.
 * `create`: an off-payer term with none — she can create a draft for its payer.
 */
export type ApplyTargetChoice = "default" | "pick" | "create";

export interface IApplyTargetView {
  /** "This term is for SuperMed, not the contract being modeled." Null when there is nothing to explain. */
  notice: string | null;
  choice: ApplyTargetChoice;
  /** The existing contracts she can pick from, labelled with their status. Empty unless `pick`. */
  options: { label: string; value: string }[];
  /** Preselected in the picker: the server's proposal when it is one of the options, else the first. */
  initialContractId: string | null;
}

export const describeApplyTarget = (
  target: IApplyContractTermTarget,
  runHasContract: boolean
): IApplyTargetView => {
  if (!target.requiresChoice) {
    return {
      notice: null,
      choice: "default",
      options: [],
      initialContractId: null,
    };
  }
  const notice = runHasContract
    ? `This term is for ${target.payerName}, not the contract being modeled.`
    : `This term is for ${target.payerName}, not the payer being modeled.`;
  const options = target.existingCandidates.map((c) => ({
    value: c.id,
    label: `${c.name} · ${c.status}`,
  }));
  if (options.length === 0) {
    return { notice, choice: "create", options, initialContractId: null };
  }
  const proposed = options.some((o) => o.value === target.contractFeeScheduleId)
    ? target.contractFeeScheduleId
    : null;
  return {
    notice,
    choice: "pick",
    options,
    initialContractId: proposed ?? options[0].value,
  };
};

/**
 * What the request must carry for the choice. `default` sends nothing (the server knows the home); an
 * off-payer save ALWAYS names its target — the server refuses one that does not.
 */
export const applyTargetRequest = (
  choice: ApplyTargetChoice,
  selectedContractId: string | null
): Pick<
  IApplyContractTermRequest,
  "targetContractFeeScheduleId" | "createNewContract"
> => {
  if (choice === "pick" && selectedContractId) {
    return { targetContractFeeScheduleId: selectedContractId };
  }
  if (choice === "create") return { createNewContract: true };
  return {};
};

/** CLE-2811 (2026-09-28) — the save is a draft of her work (Ani: "It should be Save Draft"). */
export const SAVE_DRAFT = "Save draft";
export const SAVE_AS_NEW_DRAFT_CONTRACT = "Save as a new draft contract";

/** The save button: where it writes, in words. */
export const saveButtonLabel = (
  target: IApplyContractTermTarget,
  choice: ApplyTargetChoice,
  selectedContractName: string | null
): string => {
  if (choice === "pick") {
    return selectedContractName
      ? `${SAVE_DRAFT} to ${selectedContractName}`
      : SAVE_DRAFT;
  }
  if (choice === "create") {
    return `${SAVE_AS_NEW_DRAFT_CONTRACT} for ${target.payerName}`;
  }
  return target.mode === "create" ? SAVE_AS_NEW_DRAFT_CONTRACT : SAVE_DRAFT;
};

const documentCount = (n: number): string =>
  `${n} document${n === 1 ? "" : "s"}`;

/**
 * The one line about the run's documents. A NEW contract gets copies of them (its own files); an
 * existing contract never does, and an off-payer save says so, since she might expect otherwise.
 * `saved` = after the real save (what happened) rather than on the preview (what will).
 */
export const documentsLine = (
  target: IApplyContractTermTarget,
  documents: IApplyContractTermDocuments | null,
  saved: boolean
): string | null => {
  if (!documents) {
    return target.requiresChoice
      ? `This ${CONTRACT_MODEL}'s documents are not copied to an existing contract.`
      : null;
  }
  const { copied, notCopied } = documents;
  if (copied.length === 0 && notCopied.length === 0) {
    return `This ${CONTRACT_MODEL} has no documents to copy to the new contract.`;
  }
  const skipped =
    notCopied.length > 0
      ? ` Not copied: ${notCopied.join(", ")}${
          saved ? " — upload them on the contract page." : "."
        }`
      : "";
  if (copied.length === 0) return skipped.trim();
  return saved
    ? `${documentCount(copied.length)} copied to the new contract.${skipped}`
    : `This ${CONTRACT_MODEL}'s ${documentCount(
        copied.length
      )} will be copied to the new contract.${skipped}`;
};

/** The success message after a real save. */
export const savedMessage = (result: IApplyContractTermResult): string => {
  const { target } = result;
  if (result.created) {
    return `A draft contract was created for ${target.payerName}. Review and activate it in Payer Rules.`;
  }
  if (target.mode === "create") {
    // Idempotent: this term already created a contract, and asking again returns it.
    return `This term already created a draft contract for ${target.payerName} — nothing new was created.`;
  }
  const where =
    target.mode === "other_contract" && target.contractName
      ? target.contractName
      : "the contract";
  if (!result.applied) {
    return `Nothing to save — ${where} already had every field the agent found.`;
  }
  const replaced = result.changes.filter((c) => c.action === "replaced").length;
  if (replaced > 0) {
    return `Saved to ${where}. ${replaced} text field${
      replaced === 1 ? " was" : "s were"
    } replaced; everything else that already had a value was left alone.`;
  }
  return `Saved to ${where}. Fields that already had a value were left alone.`;
};

// ---------------------------------------------------------------------------------------------
// CLE-2811 — a preview is true only for the term's values it was computed from
// ---------------------------------------------------------------------------------------------

/**
 * JSON with object keys sorted at every level, so two copies of the same values — the ones she saved and
 * the ones Postgres `jsonb` hands back with the keys reordered — give the same string. `undefined` members
 * are dropped, as `JSON.stringify` drops them; arrays keep their order.
 */
export const stableStringify = (value: unknown): string => {
  const sortKeys = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(sortKeys);
    if (v && typeof v === "object") {
      const obj = v as Record<string, unknown>;
      return Object.keys(obj)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = sortKeys(obj[key]);
          return acc;
        }, {});
    }
    return v;
  };
  return JSON.stringify(sortKeys(value)) ?? "";
};

/** Something the apply panel worked out, with the term's values (`stableStringify`) it was worked out for. */
export interface IForValues<T> {
  valuesKey: string;
  value: T;
}

/**
 * The value while the term still has the values it was computed for, else null. This is what clears the
 * panel's preview, target, picked contract, error and finished save when the accepted values change —
 * she re-confirmed with edits, a regeneration moved them, a facts-first schedule filled a slot — so Save
 * always waits for a dry run of the values it will write. A dry run that answers after the values moved
 * lands under the old values and is never shown.
 */
export const forValues = <T>(
  entry: IForValues<T> | null,
  valuesKey: string
): T | null => (entry && entry.valuesKey === valuesKey ? entry.value : null);
